import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  X,
  Coins,
  CreditCard,
  Check,
  Loader2,
  ExternalLink,
  Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const BuyCreditsModal = ({ isOpen, onClose, onSuccess, currentCredits = 0 }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Configure axios for httpOnly cookies
  const authAxios = axios.create({
    withCredentials: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/api/public/packages`);
      setPackages(response.data || []);
      // Auto-select most popular (middle option)
      if (response.data?.length > 0) {
        const midIndex = Math.floor(response.data.length / 2);
        setSelectedPackage(response.data[midIndex]);
      }
    } catch (err) {
      setError("Failed to load packages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    setProcessing(true);
    setError(null);

    try {
      const response = await authAxios.post(
        `${API}/api/checkout/create-session`,
        {
          package_id: selectedPackage.id,
          origin_url: window.location.origin,
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError("Failed to create checkout session");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
      setProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatCredits = (credits) => {
    return new Intl.NumberFormat('en-US').format(credits);
  };

  const getPackageValue = (pkg) => {
    // Calculate value (credits per dollar)
    return (pkg.credits / pkg.price).toFixed(1);
  };

  const getBestValue = () => {
    if (packages.length === 0) return null;
    return packages.reduce((best, pkg) => {
      const value = pkg.credits / pkg.price;
      const bestValue = best.credits / best.price;
      return value > bestValue ? pkg : best;
    });
  };

  const bestValuePkg = getBestValue();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-slate-800 rounded-2xl w-full max-w-lg border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-amber-500/20 to-purple-500/20 p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Buy Credits</h2>
                  <p className="text-gray-400 text-sm">
                    Current balance: <span className="text-amber-400 font-medium">{formatCredits(currentCredits)}</span>
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
              </div>
            ) : error && packages.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-red-400 mb-4">{error}</p>
                <button
                  onClick={fetchPackages}
                  className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                {/* Packages Grid */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => setSelectedPackage(pkg)}
                      className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                        selectedPackage?.id === pkg.id
                          ? "border-amber-500 bg-amber-500/10"
                          : "border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10"
                      }`}
                      data-testid={`package-option-${pkg.id}`}
                    >
                      {/* Best Value Badge */}
                      {bestValuePkg?.id === pkg.id && (
                        <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-gradient-to-r from-amber-500 to-amber-600 text-black text-xs font-bold rounded-full flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Best Value
                        </div>
                      )}
                      
                      {/* Selected Check */}
                      {selectedPackage?.id === pkg.id && (
                        <div className="absolute top-2 left-2 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                          <Check className="w-3 h-3 text-black" />
                        </div>
                      )}
                      
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-1 mb-1">
                          <Coins className="w-5 h-5 text-amber-400" />
                          <span className="text-2xl font-bold text-white">{formatCredits(pkg.credits)}</span>
                        </div>
                        <p className="text-lg font-semibold text-green-400">{formatPrice(pkg.price)}</p>
                        <p className="text-gray-500 text-xs mt-1">
                          {getPackageValue(pkg)} credits/$
                        </p>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                {/* Summary */}
                {selectedPackage && (
                  <div className="bg-white/5 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">You'll receive</span>
                      <span className="text-amber-400 font-bold text-lg flex items-center gap-1">
                        <Coins className="w-4 h-4" />
                        {formatCredits(selectedPackage.credits)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-400">Total cost</span>
                      <span className="text-green-400 font-bold text-lg">{formatPrice(selectedPackage.price)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/10">
                      <span className="text-gray-400">New balance</span>
                      <span className="text-white font-bold text-lg flex items-center gap-1">
                        <Coins className="w-4 h-4 text-amber-400" />
                        {formatCredits(currentCredits + selectedPackage.credits)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Purchase Button */}
                <button
                  onClick={handlePurchase}
                  disabled={!selectedPackage || processing}
                  className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="buy-credits-submit"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5" />
                      Pay {selectedPackage ? formatPrice(selectedPackage.price) : ""}
                      <ExternalLink className="w-4 h-4 ml-1" />
                    </>
                  )}
                </button>

                {/* Security Note */}
                <p className="text-center text-gray-500 text-xs mt-4">
                  Secure payment powered by Stripe. You'll be redirected to complete your purchase.
                </p>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BuyCreditsModal;
